import axios from "axios";
import React, { useEffect, useState } from "react";
import { Search, Filter, Download, User, Mail, Phone, Calendar, TrendingUp, Loader2 } from 'lucide-react';
import styles from './LeadsDashboard.module.css';

const LeadsDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ campaign_id: "", search: "", page: 1, limit: 25 });
  const userId = localStorage.getItem('userId');
  const API_BASE = "https://prod.toxboxai.com";

  useEffect(() => {
    if (userId) {
      fetchCampaigns();
      fetchLeads();
    }
  }, [userId, filters]);

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/meta/${userId}/campaigns`);
      if (res.data.success) setCampaigns(res.data.campaigns);
    } catch (err) { }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters);
      const url = `${API_BASE}/api/meta/${userId}/leads?${params}`;
      const res = await axios.get(url);
      if (res.data.success) {
        setLeads(res.data.leads);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Campaign", "Created At"];
    const rows = leads.map(l => [
      l.name || "",
      l.email || "",
      l.phone || "",
      campaigns.find(c => c.id === l.campaign_id)?.name || "Unknown",
      new Date(l.created_time).toLocaleString()
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const campaignMap = Object.fromEntries(campaigns.map(c => [c.id, c.name]));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <h2>Leads Dashboard</h2>
          <span className={styles.count}>{pagination.total_leads || 0} total</span>
        </div>
        <button onClick={exportCSV} className={styles.exportBtn}>
          <Download size={18} /> Export CSV
        </button>
      </div>

      <div className={styles.filters}>
        <div className={styles.search}>
          <Search size={18} />
          <input
            placeholder="Search leads..."
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
        </div>
        <select
          value={filters.campaign_id}
          onChange={e => setFilters({ ...filters, campaign_id: e.target.value, page: 1 })}
        >
          <option value="">All Campaigns</option>
          {campaigns.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.loader}><Loader2 className="spin" /> Loading...</div>
      ) : leads.length === 0 ? (
        <div className={styles.empty}>No leads found</div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th><User size={16} /> Name</th>
                  <th><Mail size={16} /> Email</th>
                  <th><Phone size={16} /> Phone</th>
                  <th><TrendingUp size={16} /> Campaign</th>
                  <th><Calendar size={16} /> Created</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(l => (
                  <tr key={l.lead_id}>
                    <td>{l.name || "-"}</td>
                    <td>{l.email || "-"}</td>
                    <td>{l.phone || "-"}</td>
                    <td>{campaignMap[l.campaign_id] || "Unknown"}</td>
                    <td>{new Date(l.created_time).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.pagination}>
            <button
              disabled={filters.page === 1}
              onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            >
              Previous
            </button>
            <span>Page {filters.page} of {pagination.total_pages}</span>
            <button
              disabled={filters.page === pagination.total_pages}
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LeadsDashboard;